import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { locationApi, shippingApi } from '../utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Send } from 'lucide-react';

export const ApiTester = () => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Location params
  const [provinceId, setProvinceId] = useState('');
  const [cityId, setCityId] = useState('');
  const [districtId, setDistrictId] = useState('');
  
  // Shipping params
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [weight, setWeight] = useState('');
  const [courier, setCourier] = useState('jne');
  
  // Tracking params
  const [awb, setAwb] = useState('');
  const [trackCourier, setTrackCourier] = useState('jne');
  const [lastPhone, setLastPhone] = useState('');
  
  const handleRequest = async (endpoint) => {
    setLoading(true);
    setResponse(null);
    
    try {
      let result;
      
      switch (endpoint) {
        case 'provinces':
          result = await locationApi.getProvinces();
          break;
        case 'cities':
          result = await locationApi.getCities(provinceId || null);
          break;
        case 'districts':
          result = await locationApi.getDistricts(cityId || null);
          break;
        case 'subdistricts':
          result = await locationApi.getSubdistricts(districtId || null);
          break;
        case 'shipping':
          if (!origin || !destination || !weight) {
            toast.error('Please fill in all shipping fields');
            return;
          }
          result = await shippingApi.calculateCost({
            origin: parseInt(origin),
            destination: parseInt(destination),
            weight: parseInt(weight),
            courier
          });
          break;
        case 'tracking':
          if (!awb || !trackCourier) {
            toast.error('Please fill in AWB and courier');
            return;
          }
          const token = localStorage.getItem('token');
          result = await axios.post(
            `${BACKEND_URL}/api/v1/track`,
            {
              awb: awb.trim(),
              courier: trackCourier,
              last_phone_number: lastPhone.trim() || null
            },
            {
              headers: {
                'X-API-Key': token
              }
            }
          );
          break;
        default:
          break;
      }
      
      setResponse(result.data);
      toast.success('Request successful');
    } catch (error) {
      const errorData = error.response?.data || { error: error.message };
      setResponse(errorData);
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="api-tester-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">API Tester</h1>
        <p className="text-gray-600 mt-2">Test API endpoints (JWT authenticated)</p>
      </div>
      
      {/* Endpoints */}
      <Tabs defaultValue="locations" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="locations" data-testid="locations-tab">Location APIs</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="shipping-tab">Shipping Cost</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tracking-tab">Track Package</TabsTrigger>
        </TabsList>
        
        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Location Endpoints</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Get Provinces</label>
                  <Button
                    onClick={() => handleRequest('provinces')}
                    disabled={loading}
                    className="w-full"
                    data-testid="test-provinces-button"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    GET /v1/province
                  </Button>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Get Cities</label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Province ID (optional)"
                      value={provinceId}
                      onChange={(e) => setProvinceId(e.target.value)}
                      data-testid="province-id-input"
                    />
                    <Button
                      onClick={() => handleRequest('cities')}
                      disabled={loading}
                      data-testid="test-cities-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Get Districts</label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="City ID (optional)"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                      data-testid="city-id-input"
                    />
                    <Button
                      onClick={() => handleRequest('districts')}
                      disabled={loading}
                      data-testid="test-districts-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Get Subdistricts</label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="District ID (optional)"
                      value={districtId}
                      onChange={(e) => setDistrictId(e.target.value)}
                      data-testid="district-id-input"
                    />
                    <Button
                      onClick={() => handleRequest('subdistricts')}
                      disabled={loading}
                      data-testid="test-subdistricts-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="shipping" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calculate Shipping Cost</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Origin (District ID)</label>
                  <Input
                    placeholder="e.g., 1391"
                    value={origin}
                    onChange={(e) => setOrigin(e.target.value)}
                    data-testid="origin-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Destination (District ID)</label>
                  <Input
                    placeholder="e.g., 1376"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    data-testid="destination-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Weight (grams)</label>
                  <Input
                    placeholder="e.g., 1000"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    data-testid="weight-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Courier</label>
                  <Select value={courier} onValueChange={setCourier}>
                    <SelectTrigger data-testid="courier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="jne">JNE</SelectItem>
                      <SelectItem value="tiki">TIKI</SelectItem>
                      <SelectItem value="pos">POS Indonesia</SelectItem>
                      <SelectItem value="jnt">J&T Express</SelectItem>
                      <SelectItem value="sicepat">SiCepat Express</SelectItem>
                      <SelectItem value="lion">Lion Parcel</SelectItem>
                      <SelectItem value="ninja">Ninja Xpress</SelectItem>
                      <SelectItem value="ide">ID Express</SelectItem>
                      <SelectItem value="anteraja">AnterAja</SelectItem>
                      <SelectItem value="sap">SAP Express</SelectItem>
                      <SelectItem value="rex">REX Kiriman Express</SelectItem>
                      <SelectItem value="rpx">RPX</SelectItem>
                      <SelectItem value="wahana">Wahana Prestasi Logistik</SelectItem>
                      <SelectItem value="ncs">Nusantara Card Semesta</SelectItem>
                      <SelectItem value="sentral">Sentral Cargo</SelectItem>
                      <SelectItem value="star">Star Cargo</SelectItem>
                      <SelectItem value="dse">21 Express / DSE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={() => handleRequest('shipping')}
                disabled={loading}
                className="w-full"
                data-testid="test-shipping-button"
              >
                <Send className="h-4 w-4 mr-2" />
                POST /v1/cost
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="tracking" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Track Package by AWB</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">AWB / Tracking Number</label>
                  <Input
                    placeholder="e.g., JNE12345678"
                    value={awb}
                    onChange={(e) => setAwb(e.target.value)}
                    data-testid="awb-input"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Courier</label>
                  <Select value={trackCourier} onValueChange={setTrackCourier}>
                    <SelectTrigger data-testid="track-courier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="jne">JNE</SelectItem>
                      <SelectItem value="tiki">TIKI</SelectItem>
                      <SelectItem value="pos">POS Indonesia</SelectItem>
                      <SelectItem value="jnt">J&T Express</SelectItem>
                      <SelectItem value="sicepat">SiCepat Express</SelectItem>
                      <SelectItem value="lion">Lion Parcel</SelectItem>
                      <SelectItem value="ninja">Ninja Xpress</SelectItem>
                      <SelectItem value="ide">ID Express</SelectItem>
                      <SelectItem value="anteraja">AnterAja</SelectItem>
                      <SelectItem value="sap">SAP Express</SelectItem>
                      <SelectItem value="rex">REX Kiriman Express</SelectItem>
                      <SelectItem value="rpx">RPX</SelectItem>
                      <SelectItem value="wahana">Wahana</SelectItem>
                      <SelectItem value="ncs">NCS</SelectItem>
                      <SelectItem value="sentral">Sentral Cargo</SelectItem>
                      <SelectItem value="star">Star Cargo</SelectItem>
                      <SelectItem value="dse">DSE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Last 5 Digits Phone (Optional)</label>
                  <Input
                    placeholder="12345"
                    value={lastPhone}
                    onChange={(e) => setLastPhone(e.target.value)}
                    maxLength={5}
                    data-testid="phone-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required for JNE tracking</p>
                </div>
              </div>
              <Button
                onClick={() => handleRequest('tracking')}
                disabled={loading}
                className="w-full"
                data-testid="test-tracking-button"
              >
                <Send className="h-4 w-4 mr-2" />
                POST /v1/track
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Response */}
      {response && (
        <Card>
          <CardHeader>
            <CardTitle>Response</CardTitle>
          </CardHeader>
          <CardContent>
            <SyntaxHighlighter
              language="json"
              style={vscDarkPlus}
              customStyle={{ borderRadius: '0.5rem', maxHeight: '500px' }}
            >
              {JSON.stringify(response, null, 2)}
            </SyntaxHighlighter>
          </CardContent>
        </Card>
      )}
    </div>
  );
};